import { createContext, useContext, type ReactNode } from 'react';
import type { I18n, MediaAsset, MediaKind } from '@/lib/api/types';

/**
 * Decouples the schema-form media/entity pickers (§4.7) from the feature modules that
 * implement them. The app shell provides resolvers; widgets call them to get an id back.
 */
export interface PickerApi {
  /** Open the media library picker; resolves to a media id or null if cancelled. */
  pickMedia: (opts?: MediaPickOptions) => Promise<string | null>;
  /**
   * Like pickMedia, but resolves to the whole asset — for callers that store more than the id
   * (a page-copy image node also records the asset's width and height).
   */
  pickMediaAsset: (opts?: MediaPickOptions) => Promise<MediaAsset | null>;
  /** Open a content collection picker; resolves to an entity id or null. */
  pickEntity: (collection: string) => Promise<string | null>;
  /** Render a small thumbnail/label for a chosen media id (for the picker field preview). */
  MediaPreview: (props: { id: string | null }) => ReactNode;
  /**
   * The pixel size of a chosen asset, or null while it is unknown. Read by `x-media-min`
   * placements; null covers "not loaded yet" as well as "no such asset", and the caller must
   * treat both as "say nothing" — a spinner rendered as "too small" would be worse than silence.
   */
  mediaDimensions: (id: string) => { width: number; height: number } | null;
  /**
   * Why this file would be refused if uploaded (unsupported type, over the size cap), as a
   * sentence for the editor, or null when it is acceptable. The same pre-flight the media
   * library's dropzone runs; the server stays the authority (it checks magic bytes).
   */
  checkUpload: (file: File) => string | null;
  /**
   * Upload one image with its bilingual alt text (`POST /admin/media`); resolves to the new
   * asset's id. Rejects with an `Error` whose message is already a sentence for the editor
   * (the server's reason for THIS file), so a gallery can show it on the row. It lives on this
   * context, beside `pickMedia`, so a multi-upload never mounts a second media host.
   */
  uploadImage: (file: File, alt: I18n) => Promise<string>;
}

export interface MediaPickOptions {
  restricted?: boolean;
  /** Only offer assets of this kind, or of these kinds (the browser's kind filter is locked). */
  kind?: MediaKind | readonly MediaKind[];
}

const noop: PickerApi = {
  pickMedia: async () => null,
  pickMediaAsset: async () => null,
  pickEntity: async () => null,
  MediaPreview: () => null,
  mediaDimensions: () => null,
  checkUpload: () => null,
  uploadImage: async () => {
    throw new Error('No media host is mounted.');
  },
};

const PickerContext = createContext<PickerApi>(noop);

export function PickerProvider({ value, children }: { value: PickerApi; children: ReactNode }) {
  return <PickerContext.Provider value={value}>{children}</PickerContext.Provider>;
}

export function usePicker(): PickerApi {
  return useContext(PickerContext);
}
